import type { SimpleMarketplaceResponse } from '@yoop/server-model/marketplace';
import { ServerMarketplaceState, ServerMarketplaceType } from '@yoop/server-model/marketplace';
import { datetime } from '@yoop/datetime';
import type { EventTransactingTool } from '@yoop/whitelabel-data';
import { TransactingToolType } from '@yoop/whitelabel-data';
import isNil from 'ramda/src/isNil';
import { requestedOffersInfoFrom } from './offer.helper';

export const transactingToolFrom = (
  marketplace: SimpleMarketplaceResponse,
  allInPrice: boolean,
): EventTransactingTool | undefined => {
  const type = marketplaceTypeFrom(marketplace?.type);
  if (isNil(type)) {
    return undefined;
  }
  return {
    type,
    canBuyAddons: !isNil(marketplace.addOnMinimumPrice) && marketplace.addOnMinimumPrice >= 0,
    canBuyMerchandise: !isNil(marketplace.merchMinimumPrice) && marketplace.merchMinimumPrice >= 0,
    canBuyYoop:
      !isNil(marketplace.minimumPrice) &&
      (isNil(marketplace.userTicketLimitRemaining) || marketplace.userTicketLimitRemaining > 0),
    endTime: marketplace.endDateTime ?? '',
    startTime: marketplace.startDateTime ?? '',
    id: marketplace.id,
    pendingOffers: requestedOffersInfoFrom(marketplace).offers,
    minPrice: (allInPrice ? marketplace.allInMinimumPrice : marketplace.minimumPrice) ?? 0,
    maxPrice: (allInPrice ? marketplace.allInMaximumPrice : marketplace.maximumPrice) ?? 0,
  };
};

// Same wire-to-client switch also exists boundary-locally in
// libs/whitelabel/whitelabel-data-loader/src/lib/events.helper.ts,
// libs/whitelabel/whitelabel-event/src/lib/event.helper.ts and
// libs/consumer-app/transaction/event-marketplace-helper/src/lib/marketplace-mapper.ts
// (module-boundary tags prevent a single shared host). Keep them in sync.
const marketplaceTypeFrom = (
  marketplaceType: ServerMarketplaceType,
): TransactingToolType | undefined => {
  switch (marketplaceType) {
    case ServerMarketplaceType.PURCHASE:
      return TransactingToolType.DirectPurchase;
    case ServerMarketplaceType.SECONDARY_EXCHANGE:
      return TransactingToolType.Resale;
    case ServerMarketplaceType.AUCTION:
      return TransactingToolType.MyPrice;
    case ServerMarketplaceType.DRAW:
      return TransactingToolType.WishList;
    default:
      return undefined;
  }
};

//@FIXME: merge with libs/util-marketplace/src/lib/predicate/active.ts
export const isMarketplaceActive = (resale: SimpleMarketplaceResponse) => {
  if (!resale || resale.state === ServerMarketplaceState.CANCELLED) {
    return false;
  }
  const start = resale.startDateTime ? datetime.parse(resale.startDateTime) : undefined;
  const end = resale.endDateTime ? datetime.parse(resale.endDateTime) : undefined;
  if (isNil(start) || isNil(end)) {
    return false;
  }
  return datetime.isLive(start, end, 'millisecond');
};

//Duplicated from libs/util-marketplace/src/lib/helper/get-marketplace-type-priority.ts
export const getMarketplaceTypePriority = (type: ServerMarketplaceType): number => {
  // Draw and auction marketplaces are given the highest priority when
  // multiple marketplaces are active, followed by purchase and finally
  // resale. Because resale can be active at any point in the event, we
  // should only be providing that option to the user if nothing else is
  // open.
  switch (type) {
    case ServerMarketplaceType.DRAW:
    case ServerMarketplaceType.AUCTION:
      return 0;
    case ServerMarketplaceType.PURCHASE:
      return 1;
    default:
      return 2;
  }
};
